import { useQueryClient } from '@tanstack/react-query';
import {
  ArrowDown,
  Flame,
  GitCompareArrows,
  MessagesSquare,
} from 'lucide-react';
import {
  Fragment,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

import type { CanvasCapture, ResearchRoomMessage } from '@repo/contracts';
import { Tooltip, TooltipProvider } from '@repo/ui/tooltip';

import { trackEvent } from '../analytics/posthog';
import { retestRoom, roomKeys } from '../api/research-rooms';
import { useAuth } from '../auth/use-auth';
import { hostOf } from '../browser/target-url';
import { useFixListEntries } from '../fix-list/fix-list';
import { roomFixes } from '../fix-list/room-fix-list';
import { useMobileHeaderSlot } from '../lib/mobile-header-slot';
import { Skeleton } from '../lib/skeleton';
import { LookalikesModal } from '../persona-lookalikes/lookalikes-modal';
import { PanelManager } from '../persona/panel-manager';
import { usePersonas } from '../persona/use-personas';
import { usePersonaLookalikesStore } from '../store/persona-lookalikes-store';
import {
  type Arrival,
  useResearchRoomStore,
} from '../store/research-room-store';
import { ComparisonCard } from './comparison-card';
import { Composer } from './composer';
import { pickFollowUps } from './follow-ups';
import { latestIdeas } from './ideas';
import { LookalikePollCard, LookalikePollPending } from './lookalike-poll-card';
import { addressMentions, handleOf } from './mentions';
import { PersonaCard } from './persona-card';
import { PersonaModelPicker } from './persona-model-picker';
import { PersonaRail } from './persona-rail';
import { PollCard } from './poll-card';
import { draftProblem } from './poll-draft';
import { ArrivalLog, LockedComposer, type SetupStage } from './room-arrival';
import {
  LowCreditsNote,
  RoomOutOfCredits,
  RoundCost,
  roundCosts,
  type RoundCostSpot,
  useRoomCredits,
} from './room-credits';
import {
  AnswerCard,
  FlotoCard,
  FlotoMark,
  IntroLine,
  QuestionBubble,
  Working,
} from './room-messages';
import { RoomScreensPicker } from './room-screens-picker';
import { EndSessionButton, SessionEndedBar } from './session-end';
import { usePacedThread } from './use-paced-thread';
import { useResearchRoom } from './use-research-room';

/** How close to the bottom still counts as "following along", in px. */
const FOLLOW_SLACK = 120;
/** Space left above a question just asked, once it is brought to the top. */
const QUESTION_TOP_GAP = 16;

/** How far the thread's last real line sits below the view, in px — not
 *  counting the room left under a question just asked. */
function contentBelow(list: HTMLElement): number {
  const spacer = list.querySelector<HTMLElement>('[data-thread-spacer]');
  const bottom = spacer ? spacer.offsetTop : list.scrollHeight;
  return bottom - list.scrollTop - list.clientHeight;
}

/** How long the pointer rests on a line before its place lights up: a
 *  pointer passing over the thread on its way somewhere is not a question. */
const HOVER_INTENT_MS = 450;
/** And how long after it leaves before the light goes — moving between two
 *  lines should not blink it off and on. */
const HOVER_LEAVE_MS = 200;

/** How an answer the write-up cites flashes in the thread. */
const CITED = ['rounded-card', 'ring-2', 'ring-idea', 'transition-shadow'];

/**
 * The research room, fixed beside the board.
 *
 * A sibling of the canvas rather than an overlay on it, so the board keeps
 * panning and zooming while the room talks — the screen being discussed is
 * the thing the researcher most wants to look at. The room shown is the
 * session's chat, on its context's screen (`capture`) — selecting another
 * card on the board, or none, leaves it where it is.
 *
 * Deliberately quiet: one card per speaker, Floto's read only once a round is
 * over, and the next questions offered as chips rather than a menu of
 * everything that could be asked.
 */
export function ResearchRoomPanel({
  capture,
  captures = [],
  enter = true,
  hasCaptures,
  mobile = false,
  onSelectCapture,
  pending = false,
  projectId,
}: {
  /** The chat's context — the screen its room began on — or, before there
   *  is a room, the screen one would be opened on. */
  capture: CanvasCapture | null;
  /** Every screen on the board — what a re-test can be run on. */
  captures?: CanvasCapture[];
  /**
   * Slide in on mount. Off when taking over from the walk's room, which sat
   * in the same place with the same header and thread — there nothing should
   * move; the next note simply arrives.
   */
  enter?: boolean;
  /** Opens another screen's room, for a re-test or to go back to its
   *  original. */
  onSelectCapture?: (captureId: string) => void;
  /** Whether the board has anything on it — a panel is only written from a
   *  capture, so this is what says one is on its way. */
  hasCaptures: boolean;
  /** The phone layout: the whole screen, nothing hung off its edges. The
   *  roster and profiles have their own tab there, and the re-test and
   *  heatmap need the board, which a phone doesn't get. */
  mobile?: boolean;
  projectId?: string;
  /** The session and its screens are still loading: shapes, not "select a
   *  screen", which is not true yet. */
  pending?: boolean;
}) {
  const captureId = capture?.id ?? null;
  const {
    ask,
    askLookalikePoll,
    replay,
    drafts,
    error,
    loading,
    lookalikeSending,
    messages,
    room,
    sending,
    shortfall,
    start,
    starting,
    thinking,
    end,
    ending,
  } = useResearchRoom(captureId);
  const roomCredits = useRoomCredits(room?.lastRoundCredits, shortfall);
  // The room talks like a group chat: one person at a time, each typing
  // before their answer lands. The stream is as fast as it is; this only
  // decides when each line is drawn.
  const paced = usePacedThread(room?.id, messages);
  const typingNow = paced.stage !== null && Date.now() >= paced.stage.showsAt;

  /** Answering, or still playing the answers out: the room is talking. */
  const talking = thinking || paced.playing;
  const {
    building,
    loaded: panelLoaded,
    personas: panel,
  } = usePersonas({
    building: hasCaptures,
    projectId,
  });
  const { user } = useAuth();

  const suggest = useResearchRoomStore((s) => s.suggest);
  const editPoll = useResearchRoomStore((s) => s.editPoll);
  const spotlight = useResearchRoomStore((s) => s.spotlight);
  const showSpotlight = useResearchRoomStore((s) => s.showSpotlight);
  const clearSpotlight = useResearchRoomStore((s) => s.clearSpotlight);
  const independent = useResearchRoomStore((s) => s.independent);
  const heatmap = useResearchRoomStore((s) => s.heatmap);
  const pendingReplay = useResearchRoomStore((s) => s.pendingReplay);
  const setPendingReplay = useResearchRoomStore((s) => s.setPendingReplay);
  const queryClient = useQueryClient();
  const [retestPicking, setRetestPicking] = useState(false);
  const [retestError, setRetestError] = useState<string | null>(null);

  // A re-test starts where its room lives: once its screen is open here and
  // the room is quiet, run the replay.
  useEffect(() => {
    if (!room || room.id !== pendingReplay || thinking) return;
    setPendingReplay(null);
    setFollowing(true);
    replay();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [room?.id, pendingReplay, thinking]);

  const otherScreens = captures.filter((c) => c.id !== capture?.id);
  const canRetest =
    Boolean(room) &&
    otherScreens.length > 0 &&
    messages.some(
      (m) =>
        m.status === 'done' &&
        !m.about &&
        (m.kind === 'question' || m.kind === 'poll'),
    );

  const startRetest = async (target: CanvasCapture) => {
    if (!room) return;
    setRetestPicking(false);
    setRetestError(null);
    trackEvent('retest_started', {
      room_id: room.id,
      screens: otherScreens.length,
    });
    try {
      const view = await retestRoom(room.id, target.id);
      queryClient.setQueryData(roomKeys.forCapture(target.id), view);
      // The re-test is the session's chat now.
      queryClient.setQueryData(roomKeys.forSession(view.room.sessionId), view);
      setPendingReplay(view.room.id);
      onSelectCapture?.(target.id);
    } catch (err) {
      trackEvent('retest_failed', { reason: 'start', room_id: room.id });
      setRetestError(
        err instanceof Error ? err.message : 'The re-test could not start',
      );
    }
  };
  const setHeatmap = useResearchRoomStore((s) => s.setHeatmap);
  const spotted = messages.some((m) => m.focus);
  const profileId = useResearchRoomStore((s) => s.profileId);
  const showProfile = useResearchRoomStore((s) => s.showProfile);
  const managing = useResearchRoomStore((s) => s.managing);
  const setManaging = useResearchRoomStore((s) => s.setManaging);
  // The walk that made this board, narrated on the way in — kept at the top
  // of the thread. The store only has it in the tab that watched the walk;
  // anywhere else (a reload, coming back later) the notes are rebuilt from
  // the walk's own capture — the full-page shot it put first on the board —
  // so they stay part of the room rather than vanishing. Only once there is a
  // room: before that the notes would narrate a setup that is not happening.
  const arrival = useResearchRoomStore((s) => s.arrival);
  const walked = room
    ? captures
        .filter((c) => c.sessionId === room.sessionId)
        .reduce<CanvasCapture | undefined>(
          (first, c) => (first && first.order <= c.order ? first : c),
          undefined,
        )
    : undefined;
  const arrived: (Pick<Arrival, 'host'> & { screens?: number }) | null =
    arrival && captures.some((c) => c.sessionId === arrival.sessionId)
      ? arrival
      : walked?.fullPage && walked.pageUrl
        ? { host: hostOf(walked.pageUrl) }
        : null;

  const lookalikesPersonaId = usePersonaLookalikesStore((s) => s.personaId);
  const closeLookalikes = usePersonaLookalikesStore((s) => s.close);

  // Who is in the room: its own roster once it exists, the active panel
  // before then (that is who a new room will seat).
  const byId = useMemo(
    () => new Map(panel.map((persona) => [persona.id, persona])),
    [panel],
  );
  const seated = room
    ? room.personaIds.flatMap((id) => byId.get(id) ?? [])
    : panel.filter((persona) => persona.active).slice(0, 5);

  // The rail is who is in the room — and somebody is in it once they have
  // said hello. A room opened before hellos existed seats everyone at once.
  const said = new Set(
    paced.visible
      .filter((m) => m.kind === 'intro' && m.speakerKind === 'persona')
      .map((m) => m.speakerId),
  );
  const hasHellos = messages.some(
    (m) => m.kind === 'intro' && m.speakerKind === 'persona',
  );
  const onRail = !room
    ? []
    : hasHellos
      ? seated.filter((persona) => said.has(persona.id))
      : seated;
  const profile = profileId ? byId.get(profileId) : undefined;
  const asker = user?.displayName ?? user?.email ?? 'You';

  const inputRef = useRef<HTMLTextAreaElement>(null);
  // Where the one setup note has got to, now the page is on the board.
  const setupStage: SetupStage =
    room || seated.length > 0
      ? 'ready'
      : building || !capture || loading
        ? 'panel'
        : 'nopanel';

  // The feedback starts itself, once a screen is selected and the panel is
  // seated — there is nothing left to decide, so a Start button would only be
  // one more thing to press. Not while `pending`: that is the board still
  // finding out whether this room's chat already exists (on another screen),
  // and starting before it knows would open a second one.
  const autoStartedRef = useRef(false);
  useEffect(() => {
    if (autoStartedRef.current || pending || room || !capture) return;
    if (loading || starting || error || seated.length === 0) return;
    autoStartedRef.current = true;
    start();
    // `start` is a fresh closure each render; the guard makes it once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [capture, error, loading, pending, room, seated.length, starting]);

  const listRef = useRef<HTMLDivElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);

  // Switching screens closes a card left open on the last one, and puts
  // away a spotlight on the screen left behind.
  useEffect(() => {
    showProfile(null);
    clearSpotlight();
  }, [captureId, showProfile, clearSpotlight]);

  /** The start of an answer, quoted in a follow-up so it is clear which
   *  line is being asked about. */
  const snippet = (text: string) => {
    const words = text.trim().split(/\s+/);
    return words.slice(0, 9).join(' ') + (words.length > 9 ? '…' : '');
  };

  /** "Why?" to whoever said an answer, or a challenge to everyone else —
   *  written by Floto, sent as is. */
  const followUp = (
    message: ResearchRoomMessage,
    mode: 'challenge' | 'why',
  ) => {
    const speaker = message.speakerId ? byId.get(message.speakerId) : undefined;
    const first = message.speakerName.split(' ')[0] ?? message.speakerName;
    const text =
      mode === 'why'
        ? `${speaker ? `@${handleOf(speaker, seated)} ` : ''}why do you say "${snippet(message.text)}"?`
        : `Does anyone see it differently from ${first}? "${snippet(message.text)}"`;
    setFollowing(true);
    ask({
      about: { messageId: message.id, mode },
      targets: [],
      text,
      via: 'follow_up',
    });
  };

  /** Asks the people who would push back on an answer why. */
  const askDissenters = (message: ResearchRoomMessage, ids: string[]) => {
    const first = message.speakerName.split(' ')[0] ?? message.speakerName;
    const handles = ids
      .flatMap((id) => {
        const p = byId.get(id);
        return p ? [`@${handleOf(p, seated)}`] : [];
      })
      .join(' ');
    setFollowing(true);
    ask({
      about: { messageId: message.id, mode: 'challenge' },
      targets: ids,
      text: `${handles} you'd push back on ${first} — why?`,
      via: 'dissent',
    });
  };

  // An independent round keeps every answer sealed until the last one is
  // in, then reveals them together.
  const sealedRounds = new Set(
    messages
      .filter((m) => m.kind === 'question' && m.independent)
      .map((m) => m.round)
      .filter((round) =>
        messages.some(
          (m) =>
            m.round === round && m.kind === 'answer' && m.status === 'pending',
        ),
      ),
  );

  /** Lights up the part of the screen a message is about. */
  const previewSpotlight = useResearchRoomStore((s) => s.previewSpotlight);
  const hoveredRef = useRef<string | null>(null);
  const hoverTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Lights a hovered line's place once the pointer has rested on it, or
   *  puts the light out a beat after it leaves. */
  const hoverLine = (line: ResearchRoomMessage | null) => {
    if (hoverTimer.current) clearTimeout(hoverTimer.current);
    hoverTimer.current = setTimeout(
      () => {
        hoverTimer.current = null;
        if (!line?.focus || !room) {
          previewSpotlight(null);
          return;
        }
        previewSpotlight({
          auto: true,
          captureId: line.focus.captureId ?? room.captureId,
          focus: line.focus,
          speaker:
            line.speakerKind === 'persona' && line.speakerId
              ? (byId.get(line.speakerId) ?? null)
              : null,
        });
      },
      line?.focus ? HOVER_INTENT_MS : HOVER_LEAVE_MS,
    );
  };
  // Leaving the room leaves nothing lit behind.
  useEffect(
    () => () => {
      if (hoverTimer.current) clearTimeout(hoverTimer.current);
      previewSpotlight(null);
    },
    [previewSpotlight],
  );

  const headerSlot = useMobileHeaderSlot();
  const endButton = (compact: boolean) => (
    <EndSessionButton
      compact={compact}
      disabled={talking}
      ending={ending}
      notYet={
        answered
          ? undefined
          : 'Ask the room a question first — the report is built from their answers'
      }
      onEnd={() => {
        setFollowing(true);
        end();
      }}
    />
  );

  /** "Show on the canvas" — there is no canvas on a phone to show it on. */
  const spotlightFor = (message: ResearchRoomMessage) =>
    mobile
      ? undefined
      : () => {
          spotlightOn(message, false);
        };
  const spotlightOn = (message: ResearchRoomMessage, auto: boolean) => {
    if (!message.focus || !room) return;
    if (!auto) trackEvent('location_spotlighted', { kind: message.kind });
    showSpotlight({
      auto,
      // The room's first screen for a place set before rooms held more.
      captureId: message.focus.captureId ?? room.captureId,
      focus: message.focus,
      speaker:
        message.speakerKind === 'persona' && message.speakerId
          ? (byId.get(message.speakerId) ?? null)
          : null,
    });
  };

  // A place on the screen lights up only when the reader asks for it —
  // resting on a line, or pressing its location chip. Lighting each answer's
  // place as it arrived put faces on the screenshot nobody had pointed at.

  const spotlitId = spotlight
    ? messages.find(
        (m) =>
          m.focus?.label === spotlight.focus.label &&
          m.focus.x === spotlight.focus.x &&
          m.focus.y === spotlight.focus.y,
      )?.id
    : undefined;

  // The profile card closes on Escape, or on a click anywhere that is not the
  // card or the roster (which toggles it itself).
  useEffect(() => {
    if (!profileId) return;
    const close = (event: MouseEvent | KeyboardEvent) => {
      if (event instanceof KeyboardEvent) {
        if (event.key !== 'Escape') return;
        // Claimed, so the board does not take the same Escape as leaving
        // the room.
        event.preventDefault();
        showProfile(null);
        return;
      }
      const target = event.target as Element;
      if (cardRef.current?.contains(target)) return;
      if (target.closest('[data-roster], [data-profile-trigger]')) return;
      showProfile(null);
    };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', close);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', close);
    };
  }, [profileId, showProfile]);

  // The thread never moves under the reader. New lines land where they
  // land; when one lands below what is in view, a pill says so and is the
  // way down. The one exception is the reader's own question: sending puts
  // it at the top of the view, with the room's answers to come in below it.
  const [following, setFollowing] = useState(true);
  const [unseen, setUnseen] = useState(false);
  /** Scrolled well up — more than a screen above the latest line — where
   *  the way back down is worth offering even with nothing new below. */
  const [far, setFar] = useState(false);
  useEffect(() => {
    const list = listRef.current;
    if (!list) return;
    if (contentBelow(list) <= FOLLOW_SLACK) return;
    // A phone's view holds a line or two, so answers run past it all the
    // time: one already at the bottom is carried along, chat-style, rather
    // than told there is more below.
    if (mobile && following) {
      if (typeof list.scrollTo === 'function') {
        list.scrollTo({
          behavior: 'smooth',
          top: list.scrollTop + contentBelow(list) + FOLLOW_SLACK / 2,
        });
      }
      return;
    }
    setUnseen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paced.visible.length, typingNow, paced.stage?.message.id]);
  // Sending a question brings it to the top of the thread, with just enough
  // room below for the answers to arrive in view. In that order: the room
  // has to be there first, or the thread cannot scroll that far, the
  // question stays at the bottom, and the answers land out of sight.
  /** A question asked from here this round — what the room below is for. A
   *  room opened mid-round has none: nothing to make room for. */
  const askedRef = useRef(false);
  /** The question still has to be brought up, once the room is there. */
  const anchorPending = useRef(false);
  useLayoutEffect(() => {
    if (!sending) return;
    askedRef.current = true;
    anchorPending.current = true;
  }, [sending]);
  // Keyed on the question itself landing too, not only on the "sending"
  // bubble: when the room answers fast, that bubble is never drawn.
  const latestQuestion = [...paced.visible]
    .reverse()
    .find(
      (m) =>
        (m.kind === 'question' || m.kind === 'poll') && m.status === 'done',
    )?.id;
  const seenQuestion = useRef<string | undefined>(undefined);
  useLayoutEffect(() => {
    const known = seenQuestion.current;
    seenQuestion.current = latestQuestion;
    // The first one seen is history, not something just asked.
    if (known === undefined || !latestQuestion || latestQuestion === known) {
      return;
    }
    askedRef.current = true;
    anchorPending.current = true;
  }, [latestQuestion]);
  // A room opens at its latest line — where the conversation is — once, and
  // is left alone from then on.
  const openedAt = useRef<string | undefined>(undefined);
  useLayoutEffect(() => {
    const list = listRef.current;
    if (!room || !list || openedAt.current === room.id) return;
    if (messages.length === 0) return;
    openedAt.current = room.id;
    askedRef.current = false;
    anchorPending.current = false;
    seenQuestion.current = latestQuestion;
    list.scrollTop = list.scrollHeight;
  }, [room, messages.length, latestQuestion]);
  const [spacer, setSpacer] = useState(0);
  useLayoutEffect(() => {
    const list = listRef.current;
    if (!list) return;
    // Only while the room is answering: between rounds there is nothing to
    // make room for, and nothing to scroll into.
    const active = askedRef.current && Boolean(sending || talking);
    const questions = list.querySelectorAll<HTMLElement>('[data-question]');
    const question = questions[questions.length - 1];
    let wanted = 0;
    if (active && question) {
      const present = list.querySelector<HTMLElement>('[data-thread-spacer]');
      const end = present ? present.offsetTop : list.scrollHeight;
      // From the question's top to the last real line.
      const below = end - question.offsetTop;
      wanted = Math.max(
        0,
        Math.round(list.clientHeight - QUESTION_TOP_GAP - below),
      );
    }
    if (wanted !== spacer) {
      // Runs again once the room is drawn.
      setSpacer(wanted);
      return;
    }
    if (!active) askedRef.current = false;
    if (!anchorPending.current || !question) return;
    anchorPending.current = false;
    // The thread's own scroll, set directly. `scrollIntoView` would scroll
    // every scrollable thing above it too — the page included.
    const top = Math.max(
      0,
      question.getBoundingClientRect().top -
        list.getBoundingClientRect().top +
        list.scrollTop -
        QUESTION_TOP_GAP,
    );
    // jsdom has no element scrollTo.
    if (typeof list.scrollTo === 'function') {
      list.scrollTo({ behavior: 'smooth', top });
    }
    setUnseen(false);
  }, [
    spacer,
    paced.visible.length,
    typingNow,
    sending,
    talking,
    lookalikeSending,
  ]);

  // A thread carried over from the walk opens where it was left — at its
  // latest note — before the first paint, and follows Floto's next ones.
  useLayoutEffect(() => {
    const list = listRef.current;
    if (arrived && list) list.scrollTop = list.scrollHeight;
    // Mount only: the carried-over thread is placed once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const threadShape = `${String(seated.length)}:${String(building)}`;
  useEffect(() => {
    const list = listRef.current;
    if (!arrived || room || !list) return;
    // jsdom has no element scrollTo.
    if (typeof list.scrollTo === 'function') {
      list.scrollTo({ behavior: 'smooth', top: list.scrollHeight });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threadShape]);

  // A dot on the write-up card cites an answer: bring it into view in the
  // thread and flash it, so the quote is read in its place.
  const revealMessage = useResearchRoomStore((s) => s.revealMessage);
  useEffect(() => {
    if (!revealMessage) return;
    const element = listRef.current?.querySelector<HTMLElement>(
      `[data-message-id="${revealMessage.messageId}"]`,
    );
    if (!element) return;
    setFollowing(false);
    // The thread's own scroll only — never the page's (see the question
    // anchoring above).
    const list = listRef.current;
    if (list && typeof list.scrollTo === 'function') {
      const offset =
        element.getBoundingClientRect().top -
        list.getBoundingClientRect().top +
        list.scrollTop;
      list.scrollTo({
        behavior: 'smooth',
        top: Math.max(
          0,
          offset - list.clientHeight / 2 + element.offsetHeight / 2,
        ),
      });
    }
    element.classList.add(...CITED);
    const timer = setTimeout(() => {
      element.classList.remove(...CITED);
    }, 1800);
    return () => {
      clearTimeout(timer);
      element.classList.remove(...CITED);
    };
  }, [revealMessage]);

  const jumpToLatest = () => {
    const list = listRef.current;
    if (!list) return;
    trackEvent('jumped_to_latest', { unseen });
    // To the last line, not into the room kept under a question.
    list.scrollTo({
      behavior: 'smooth',
      top: list.scrollTop + contentBelow(list) + FOLLOW_SLACK / 2,
    });
    setFollowing(true);
    setUnseen(false);
    setFar(false);
  };

  /** A question Floto wrote goes into the composer to be read, edited or
   *  sent — never straight to the room. One written for part of the room
   *  ("Elena and Priya, …") arrives as mentions, so only they are asked. */
  const pick = (question: string) => {
    suggest(addressMentions(question, seated));
    inputRef.current?.focus();
  };

  const toggleProfile = (id: string) => {
    if (profileId !== id) trackEvent('persona_profile_opened');
    showProfile(profileId === id ? null : id);
  };

  // An ended session is a record: nothing in it asks the room anything.
  const ended = Boolean(room?.endedAt);
  const quiet = talking || ended;
  // What is left to fix in this room, for the read that changed the list.
  const fixEntries = useFixListEntries(projectId);
  const openFixes =
    room && fixEntries
      ? roomFixes(fixEntries, room.id).filter((e) => e.status === 'fix').length
      : 0;
  const viewFixList = useResearchRoomStore((s) => s.viewAnalysis);
  // Follow-ups only where they earn it — see `pickFollowUps`.
  const followUps = useMemo(() => pickFollowUps(messages), [messages]);
  const askedOnce = messages.some(
    (m) => (m.kind === 'question' || m.kind === 'poll') && m.status === 'done',
  );
  const answered = messages.some(
    (message) => message.kind === 'answer' && message.status === 'done',
  );

  const costs = roundCosts(messages);
  const ideas = talking ? [] : latestIdeas(messages, byId);

  const renderMessage = (
    message: ResearchRoomMessage,
    /** The round's price, when it is drawn in this message's card. */
    cost?: RoundCostSpot,
  ) => {
    if (message.kind === 'comparison') {
      return <ComparisonCard key={message.id} message={message} />;
    }
    if (message.kind === 'context') {
      // Like "joined the room": a line about the room, not a turn in it.
      return (
        <p
          className="px-6 text-center text-tag text-text-muted duration-300 animate-in fade-in motion-reduce:animate-none"
          key={message.id}
        >
          {message.text}
        </p>
      );
    }
    if (message.kind === 'intro') {
      return (
        <div data-profile-trigger key={message.id}>
          <IntroLine
            message={message}
            onOpenProfile={toggleProfile}
            persona={
              message.speakerId ? byId.get(message.speakerId) : undefined
            }
          />
        </div>
      );
    }
    if (message.kind === 'poll') {
      return (
        <div className="space-y-3" data-question key={message.id}>
          <QuestionBubble
            asker={asker}
            message={message}
            personas={byId}
            poll
          />
          <div data-message-id={message.id} data-profile-trigger>
            <PollCard
              message={message}
              onSpotlight={spotlightFor(message)}
              onOpenProfile={toggleProfile}
              personas={byId}
              reasonsPending={thinking && message.id === messages.at(-1)?.id}
            />
          </div>
        </div>
      );
    }
    if (message.kind === 'question') {
      return (
        <div data-question key={message.id}>
          <QuestionBubble
            asker={asker}
            message={message}
            messageId={message.id}
            onSpotlight={spotlightFor(message)}
            personas={byId}
          />
        </div>
      );
    }
    if (message.kind === 'answer') {
      return (
        <div data-message-id={message.id} data-profile-trigger key={message.id}>
          <AnswerCard
            draft={drafts[message.id]}
            message={message}
            onAskDissenters={
              quiet
                ? undefined
                : (ids) => {
                    askDissenters(message, ids);
                  }
            }
            onChallenge={
              quiet || message.round !== followUps.round
                ? undefined
                : () => {
                    followUp(message, 'challenge');
                  }
            }
            onWhy={
              quiet || message.round !== followUps.round
                ? undefined
                : () => {
                    followUp(message, 'why');
                  }
            }
            suggest={{
              challenge: followUps.challenge === message.id,
              why: followUps.why === message.id,
            }}
            people={byId}
            sealed={sealedRounds.has(message.round)}
            onSpotlight={spotlightFor(message)}
            spotlit={spotlitId === message.id}
            onOpenProfile={toggleProfile}
            persona={
              message.speakerId ? byId.get(message.speakerId) : undefined
            }
          />
        </div>
      );
    }
    if (message.kind === 'lookalike-poll') {
      return (
        <LookalikePollCard key={message.id} message={message} personas={byId} />
      );
    }
    // Floto reads every round but speaks up only on a milestone; a quiet
    // read is kept for the report, not drawn — and neither is one that came
    // too late to show in the moment (see `READ_WITHIN_MS`).
    if (
      message.kind === 'summary' &&
      (message.quiet || paced.muted.has(message.id))
    ) {
      return null;
    }
    const nudge = message.nudge;
    const waiting =
      nudge !== undefined && message.id === messages.at(-1)?.id && !quiet;
    const offered = nudge?.reason === 'as-poll' ? nudge.poll : undefined;
    return (
      <FlotoCard
        fixList={
          message.kind === 'summary' && (message.fixUpdates ?? 0) > 0 && room
            ? {
                onOpen: () => {
                  trackEvent('fix_list_opened', {
                    open: openFixes,
                    source: 'summary',
                  });
                  viewFixList(
                    room.context.captureIds[0] ?? room.captureId,
                    'show',
                  );
                },
                open: openFixes,
              }
            : undefined
        }
        footer={cost ? <RoundCost {...cost} /> : undefined}
        key={message.id}
        message={message}
        onAskAnyway={
          waiting
            ? () => {
                // A held question that reads as a poll whose
                // options are not in the text: open the editor to
                // fill them in, rather than send a broken poll.
                if (
                  !offered &&
                  nudge.poll &&
                  draftProblem(nudge.poll.format, nudge.poll.options)
                ) {
                  editPoll(nudge.question, nudge.poll);
                  inputRef.current?.focus();
                  return;
                }
                setFollowing(true);
                // "Ask as a normal question" drops an offered
                // poll; "Ask it anyway" keeps a held one.
                ask({
                  poll: offered ? undefined : nudge.poll,
                  skipCheck: true,
                  targets: nudge.targets,
                  text: nudge.question,
                  via: 'nudge_anyway',
                });
              }
            : undefined
        }
        onEditPoll={
          waiting && offered
            ? () => {
                editPoll(nudge.question, offered);
                inputRef.current?.focus();
              }
            : undefined
        }
        onRunPoll={
          waiting && offered && !draftProblem(offered.format, offered.options)
            ? () => {
                setFollowing(true);
                ask({
                  poll: offered,
                  skipCheck: true,
                  targets: nudge.targets,
                  text: nudge.question,
                  via: 'nudge_poll',
                });
              }
            : undefined
        }
        onPick={
          // Floto's ideas wait behind the composer's button; only the
          // starters show, in a room nobody has answered yet, and what a
          // finished session left open.
          ended ||
          message.kind === 'summary' ||
          (message.kind === 'opener' && askedOnce)
            ? undefined
            : pick
        }
      />
    );
  };

  /** The room's own conversation, once it has one. */
  const thread = (
    <>
      {paced.visible.map((message) => {
        const cost = costs.get(message.id);
        return (
          <Fragment key={message.id}>
            {renderMessage(message, cost?.inCard ? cost : undefined)}
            {cost && !cost.inCard ? <RoundCost {...cost} /> : null}
          </Fragment>
        );
      })}
      {paced.stage && typingNow ? (
        paced.stage.message.kind === 'intro' ? (
          <IntroLine
            message={paced.stage.message}
            persona={
              paced.stage.message.speakerId
                ? byId.get(paced.stage.message.speakerId)
                : undefined
            }
            typing
          />
        ) : paced.stage.message.kind === 'answer' ? (
          <AnswerCard
            message={{ ...paced.stage.message, status: 'pending', text: '' }}
            persona={
              paced.stage.message.speakerId
                ? byId.get(paced.stage.message.speakerId)
                : undefined
            }
            typing={paced.stage.ready}
          />
        ) : (
          <div className="px-1">
            <Working>Floto is writing…</Working>
          </div>
        )
      ) : null}
      {sending ? (
        <div data-question data-sending-question>
          <QuestionBubble
            asker={asker}
            message={sending}
            personas={byId}
            poll={Boolean(sending.poll)}
            sending
          />
        </div>
      ) : null}
      {/* Just enough room below the question last asked for it to sit at
          the top of the view, so the answers arrive in sight without the
          thread scrolling to show them. It shrinks as they fill the view,
          and is never more than that — no scrolling on into blank space. */}
      {spacer > 0 ? (
        <div aria-hidden="true" data-thread-spacer style={{ height: spacer }} />
      ) : null}
      {lookalikeSending ? (
        <LookalikePollPending
          personaName={
            byId.get(lookalikeSending.personaId)?.name ?? 'a persona'
          }
          question={lookalikeSending.question}
        />
      ) : null}
    </>
  );

  return (
    <TooltipProvider>
      <aside
        className={[
          mobile
            ? 'relative flex h-full w-full min-w-0 flex-col bg-warm-alt'
            : 'relative flex h-full w-105 shrink-0 flex-col rounded-panel border border-hairline bg-warm-alt shadow-soft',
          enter && !mobile
            ? 'duration-200 animate-in slide-in-from-right-4 motion-reduce:animate-none'
            : '',
        ].join(' ')}
      >
        {/* Hanging off the panel's left edge, over the board: who is in the
            room — costing the conversation no height. (The session write-up's
            toggle, AnalysisToggle, used to lead here; it is kept, unmounted.
            The fix list is reached from the bar's pill.) */}
        {onRail.length > 0 && !mobile ? (
          <div
            className="absolute top-16 right-full z-20 mr-2 flex flex-col items-end gap-2.5"
            data-roster
          >
            <PersonaRail
              onManage={
                projectId
                  ? () => {
                      trackEvent('panel_opened', { personas: seated.length });
                      setManaging(true);
                    }
                  : undefined
              }
              onPick={toggleProfile}
              openId={profileId}
              personas={onRail}
            />
          </div>
        ) : null}

        {/* Outside the panel, over the canvas: the panel is too narrow to hold
          a profile beside the conversation, and covering the conversation to
          show one hides the thing being asked about. Level with the panel,
          which already sits below the board's top bar. */}
        {/* Behind an open profile, the board recedes — dimmed and softly
            blurred — so the card has the eye. Only the board: the room and
            its faces stay sharp, to read the thread or switch person. A
            press on it closes the card, like any press outside. */}
        {profile && !mobile ? (
          <div
            aria-hidden="true"
            className="absolute -inset-y-3 right-full z-10 w-screen bg-ink/10 backdrop-blur-xs duration-200 animate-in fade-in motion-reduce:animate-none"
          />
        ) : null}
        {profile && !mobile ? (
          <div className="pointer-events-none absolute top-0 right-full bottom-0 z-20 mr-20 flex w-120 items-center">
            <div
              className="pointer-events-auto flex max-h-full w-full flex-col"
              ref={cardRef}
            >
              {/* "Ask" and "Lookalikes" are hidden for now: the card still
                  takes onAsk / onLookalikes, so bringing them back is passing
                  them again. */}
              <PersonaCard
                key={profile.id}
                onClose={() => {
                  showProfile(null);
                }}
                persona={profile}
              />
            </div>
          </div>
        ) : null}

        {/* On a phone there is nothing to hang it beside: over the room,
            which recedes behind it; a tap outside closes it, as on the board. */}
        {profile && mobile ? (
          <div className="absolute inset-0 z-30 flex flex-col bg-ink/10 p-3 backdrop-blur-xs duration-200 animate-in fade-in motion-reduce:animate-none">
            <div className="flex max-h-full min-h-0 flex-col" ref={cardRef}>
              <PersonaCard
                key={profile.id}
                onClose={() => {
                  showProfile(null);
                }}
                persona={profile}
              />
            </div>
          </div>
        ) : null}

        {/* On a phone the app bar is the header: End session goes up there,
            and the rest of this — the title, the screen picker — is the
            board's, with one page and no room for it. */}
        {mobile && headerSlot && room && !room.endedAt
          ? createPortal(endButton(true), headerSlot)
          : null}
        <header
          className="shrink-0 rounded-t-panel border-b border-hairline bg-surface px-5 pt-4 pb-3"
          hidden={mobile}
        >
          <div className="flex min-h-8 items-center gap-1.5">
            <h2 className="!m-0 min-w-0 flex-1 !text-heading-sm !leading-tight !font-semibold !text-text-primary">
              Feedback Room
            </h2>
            {canRetest && !mobile ? (
              <div className="relative">
                <Tooltip label="Ask the same questions of another screen — say, the redesign — and compare">
                  <button
                    aria-expanded={retestPicking}
                    aria-label="Re-test on another screen"
                    className="flex h-8 shrink-0 cursor-pointer items-center gap-1.5 rounded-button px-2.5 text-ui font-medium text-text-secondary ring-1 ring-hairline transition hover:bg-surface-hover hover:text-text-primary active:scale-[0.98] disabled:cursor-default disabled:opacity-50"
                    disabled={talking}
                    onClick={() => {
                      if (!retestPicking) {
                        trackEvent('retest_menu_opened', {
                          screens: otherScreens.length,
                        });
                      }
                      setRetestPicking((open) => !open);
                    }}
                    type="button"
                  >
                    <GitCompareArrows aria-hidden="true" className="size-3.5" />
                  </button>
                </Tooltip>
                {retestPicking ? (
                  <div
                    className="absolute top-full right-0 z-30 mt-2 w-72 rounded-card bg-surface p-1.5 shadow-popover ring-1 ring-hairline duration-150 animate-in fade-in slide-in-from-top-1"
                    role="menu"
                  >
                    <p className="px-2.5 pt-1.5 pb-1 text-small-label text-text-muted uppercase">
                      Re-test on
                    </p>
                    {otherScreens.map((screen) => (
                      <button
                        className="flex w-full cursor-pointer items-center gap-2.5 rounded-button px-2 py-1.5 text-left transition hover:bg-surface-hover"
                        key={screen.id}
                        onClick={() => {
                          void startRetest(screen);
                        }}
                        role="menuitem"
                        type="button"
                      >
                        <img
                          alt=""
                          className="h-10 w-8 shrink-0 rounded-badge object-cover object-top ring-1 ring-hairline"
                          src={screen.imageUrl}
                        />
                        <span className="min-w-0 flex-1 truncate text-ui text-text-primary">
                          {screen.name ?? 'Untitled screen'}
                        </span>
                      </button>
                    ))}
                    <p className="px-2.5 pt-1 pb-1.5 text-tag leading-snug text-text-muted">
                      Same panel, same questions and polls, then Floto compares.
                    </p>
                  </div>
                ) : null}
              </div>
            ) : null}
            {room && spotted && !mobile ? (
              <Tooltip label="Where the room's attention and complaints landed on the screen">
                <button
                  aria-label="Heatmap"
                  aria-pressed={heatmap}
                  className={[
                    'flex size-9 shrink-0 cursor-pointer items-center justify-center rounded-button transition active:scale-[0.98]',
                    heatmap
                      ? 'bg-critical text-white'
                      : 'bg-tag-critical text-critical hover:brightness-95',
                  ].join(' ')}
                  onClick={() => {
                    trackEvent('heatmap_toggled', { on: !heatmap });
                    setHeatmap(!heatmap);
                  }}
                  type="button"
                >
                  <Flame aria-hidden="true" className="size-4" />
                </button>
              </Tooltip>
            ) : null}
            {room && !room.endedAt && !mobile ? endButton(false) : null}
          </div>
          {room && !room.retestOf ? (
            <RoomScreensPicker
              captures={captures}
              disabled={talking || Boolean(room.endedAt)}
              room={room}
            />
          ) : null}
          {room?.retestOf ? (
            <Tooltip label="Open the room this re-test came from">
              <button
                className="mt-2 flex max-w-full cursor-pointer items-center gap-1.5 rounded-button py-0.5 text-left text-caption text-text-secondary transition hover:text-text-primary"
                onClick={() => {
                  if (room.retestOf) onSelectCapture?.(room.retestOf.captureId);
                }}
                type="button"
              >
                <GitCompareArrows
                  aria-hidden="true"
                  className="size-3.5 shrink-0"
                />
                <span className="truncate">
                  Re-test of {room.retestOf.screenName ?? 'an earlier screen'}
                </span>
              </button>
            </Tooltip>
          ) : null}
          {retestError ? (
            <p className="mt-2 text-caption text-critical" role="alert">
              {retestError}
            </p>
          ) : null}
        </header>

        <div className="relative min-h-0 flex-1">
          <div
            className="h-full space-y-3 overflow-y-auto px-4 py-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
            onScroll={(event) => {
              const list = event.currentTarget;
              const below = contentBelow(list);
              const atBottom = below < FOLLOW_SLACK;
              setFollowing(atBottom);
              setFar(below > list.clientHeight);
              if (atBottom) setUnseen(false);
            }}
            ref={listRef}
            // Hovering a line lights its place on the board, without moving
            // it: read a line, see where it points.
            onMouseLeave={() => {
              hoveredRef.current = null;
              hoverLine(null);
            }}
            onMouseOver={(event) => {
              const id =
                (event.target as HTMLElement)
                  .closest<HTMLElement>('[data-message-id]')
                  ?.getAttribute('data-message-id') ?? null;
              if (id === hoveredRef.current) return;
              hoveredRef.current = id;
              const line = id ? messages.find((m) => m.id === id) : undefined;
              hoverLine(
                line?.focus && !sealedRounds.has(line.round) ? line : null,
              );
            }}
          >
            {arrived ? (
              <>
                <ArrivalLog
                  still
                  host={arrived.host}
                  screen={arrived.screens}
                  stage={setupStage}
                />
                <div className="mt-3 space-y-3">
                  {room ? (
                    thread
                  ) : starting ? (
                    <div className="px-1" role="status">
                      <Working>Opening the room…</Working>
                    </div>
                  ) : error && autoStartedRef.current ? (
                    // It started itself and that failed: the one way back in.
                    <button
                      className="flex h-9 cursor-pointer items-center gap-2 rounded-button bg-ink px-3.5 text-ui-strong font-semibold text-white transition hover:bg-ink/90 active:scale-[0.98]"
                      onClick={() => {
                        trackEvent('room_open_retried');
                        start();
                      }}
                      type="button"
                    >
                      <MessagesSquare aria-hidden="true" className="size-4" />
                      Try opening the room again
                    </button>
                  ) : null}
                </div>
              </>
            ) : pending || (!room && !panelLoaded && Boolean(projectId)) ? (
              <RoomSkeleton />
            ) : !capture ? (
              <EmptyState
                body="Pick a screen on the canvas — the room discusses whichever one is selected."
                title="Select a screen"
              />
            ) : loading ? (
              <RoomSkeleton />
            ) : !room ? (
              seated.length === 0 ? (
                <EmptyState
                  body={
                    building
                      ? 'Floto is writing a panel for this product. It takes a few seconds.'
                      : 'Add people to this room’s panel to get feedback.'
                  }
                  title={building ? 'Building your panel…' : 'No panel yet'}
                />
              ) : (
                <div className="flex flex-col items-center px-6 pt-12 text-center duration-300 animate-in fade-in motion-reduce:animate-none">
                  <FlotoMark size="lg" />
                  <p className="mt-4 text-heading-xs font-semibold text-text-primary">
                    Feedback on {capture.name ?? 'this screen'}
                  </p>
                  <p className="mt-1.5 text-ui leading-relaxed text-text-secondary">
                    Floto moderates. Ask a question and each persona answers in
                    turn, hearing the ones before them.
                  </p>
                  {error ? (
                    // It starts itself; when that failed, the one way back in.
                    <button
                      className="mt-5 flex h-10 cursor-pointer items-center gap-2 rounded-button bg-ink px-4 text-ui-strong font-semibold text-white transition hover:bg-ink/90 active:scale-[0.98]"
                      onClick={() => {
                        trackEvent('room_open_retried');
                        start();
                      }}
                      type="button"
                    >
                      <MessagesSquare aria-hidden="true" className="size-4" />
                      Try opening the room again
                    </button>
                  ) : (
                    <div className="mt-5" role="status">
                      <Working>Opening the room…</Working>
                    </div>
                  )}
                </div>
              )
            ) : (
              thread
            )}
          </div>

          {unseen || (!following && far) ? (
            mobile ? (
              // Out of the way of the line being read: a small round
              // button in the corner instead of a pill across the thread.
              <button
                aria-label={unseen ? 'New messages' : 'Jump to latest'}
                className="absolute right-3 bottom-3 flex size-9 cursor-pointer items-center justify-center rounded-pill bg-ink text-white shadow-popover transition duration-200 animate-in fade-in active:scale-95"
                onClick={jumpToLatest}
                type="button"
              >
                <ArrowDown aria-hidden="true" className="size-4" />
                {unseen ? (
                  <span
                    aria-hidden="true"
                    className="absolute top-0 right-0 size-2.5 rounded-pill bg-highlight ring-2 ring-surface"
                  />
                ) : null}
              </button>
            ) : (
              <button
                className="absolute bottom-3 left-1/2 flex h-8 -translate-x-1/2 cursor-pointer items-center gap-1.5 rounded-pill bg-ink px-3 text-tag font-semibold text-white shadow-popover transition duration-200 animate-in fade-in slide-in-from-bottom-1 hover:bg-ink/90"
                onClick={jumpToLatest}
                type="button"
              >
                <ArrowDown aria-hidden="true" className="size-3.5" />
                {unseen ? 'New messages' : 'Jump to latest'}
              </button>
            )
          ) : null}
        </div>

        {room ? (
          <footer
            className={`shrink-0 rounded-b-panel border-t border-hairline bg-surface ${mobile ? 'px-3 py-2' : 'px-4 pt-3 pb-4'}`}
          >
            {error ? (
              <p className="mb-2 text-caption text-critical" role="alert">
                {error}
              </p>
            ) : null}
            {room.endedAt ? (
              <SessionEndedBar room={room} />
            ) : roomCredits.blocked ? (
              <RoomOutOfCredits
                available={roomCredits.available}
                estimate={roomCredits.estimate}
              />
            ) : (
              <>
                {roomCredits.low !== null ? (
                  <LowCreditsNote
                    estimate={roomCredits.estimate}
                    questionsLeft={roomCredits.low}
                  />
                ) : null}
                <Composer
                  busy={talking}
                  ideas={ideas}
                  inputRef={inputRef}
                  onAskLookalikePoll={(personaId, question, count) => {
                    askLookalikePoll({ count, personaId, question });
                  }}
                  onPickIdea={pick}
                  onSend={(text, targets, skipCheck, poll) => {
                    setFollowing(true);
                    ask({
                      independent,
                      poll,
                      skipCheck,
                      targets,
                      text,
                      // The composer skips the check only for Floto's own
                      // question, sent as written.
                      via: skipCheck ? 'idea' : 'typed',
                    });
                  }}
                  personas={seated}
                />
                <PersonaModelPicker />
              </>
            )}
          </footer>
        ) : arrived && !error ? (
          // Still the composer's place, and still saying what unlocks it.
          <LockedComposer />
        ) : error ? (
          <p
            className="shrink-0 px-5 pb-4 text-caption text-critical"
            role="alert"
          >
            {error}
          </p>
        ) : null}

        {managing ? (
          <PanelManager
            captureId={captureId ?? undefined}
            onClose={() => {
              trackEvent('panel_closed');
              setManaging(false);
            }}
            projectId={projectId}
            room={room ?? undefined}
          />
        ) : null}

        {lookalikesPersonaId && byId.get(lookalikesPersonaId) ? (
          <LookalikesModal
            onClose={closeLookalikes}
            persona={byId.get(lookalikesPersonaId)!}
          />
        ) : null}
      </aside>
    </TooltipProvider>
  );
}

/** The shape of a conversation while the room loads — a blank panel reads
 *  as broken, and the real cards land in the same places. */
/** One answer, as a shape: a face, a name, a few lines. */
function AnswerSkeleton({ lines }: { lines: string[] }) {
  return (
    <div className="rounded-card bg-surface px-4 py-3.5 ring-1 ring-hairline">
      <div className="flex items-center gap-2.5">
        <Skeleton className="size-8 rounded-md" />
        <div className="space-y-1.5">
          <Skeleton className="h-3 w-24 rounded-pill" />
          <Skeleton className="h-2.5 w-16 rounded-pill" />
        </div>
      </div>
      <div className="mt-3 space-y-2">
        {lines.map((width) => (
          <Skeleton className={`h-3 rounded-pill ${width}`} key={width} />
        ))}
      </div>
    </div>
  );
}

/** The room on its way: the shape of a conversation, arriving in turn. */
function RoomSkeleton() {
  return (
    <div
      aria-busy="true"
      aria-label="Loading the room"
      className="space-y-3"
      role="status"
    >
      <Skeleton className="ml-auto h-12 w-3/4 rounded-card" />
      <div className="duration-500 animate-in fade-in fill-mode-backwards motion-reduce:animate-none">
        <AnswerSkeleton lines={['w-full', 'w-11/12', 'w-2/3']} />
      </div>
      <div className="delay-150 duration-500 animate-in fade-in fill-mode-backwards motion-reduce:animate-none">
        <AnswerSkeleton lines={['w-full', 'w-3/5']} />
      </div>
    </div>
  );
}

function EmptyState({ body, title }: { body: string; title: string }) {
  return (
    <div className="px-8 pt-16 text-center duration-300 animate-in fade-in motion-reduce:animate-none">
      <p className="text-ui-strong font-semibold text-text-primary">{title}</p>
      <p className="mt-1.5 text-ui leading-relaxed text-text-muted">{body}</p>
    </div>
  );
}
